// src/component/homepage/HomepagePricingPage.jsx
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  CheckCircle,
  Sparkles,
  Rocket,
  Zap,
  Building2,
  Dot,
} from "lucide-react";

/* ---------- Static Pricing (homepage-safe) ---------- */
const pricingData = {
  currencies: [
    { id: "USD", symbol: "$", name: "USA (USD)" },
    { id: "GBP", symbol: "£", name: "UK (GBP)" },
    { id: "CAD", symbol: "C$", name: "Canada (CAD)" },
    { id: "AUD", symbol: "A$", name: "Australia (AUD)" },
    { id: "INR", symbol: "₹", name: "India (INR)" },
  ],
  plans: [
    {
      id: 1,
      title: "Starter",
      credits: "500",
      prices: { USD: "49", GBP: "39.99", CAD: "69.99", AUD: "79.99", INR: "3,499" },
      recommended: false,
      coverage: [
        "CONSERVATIVE – 108 prospects without buying intent, 76 with buying intent",
        "BALANCED – 52 prospects without buying intent, 43 with buying intent",
        "AGGRESSIVE – 52 prospects without buying intent, 43 with buying intent",
      ],
    },
    {
      id: 2,
      title: "Growth",
      credits: "1,000",
      prices: { USD: "99", GBP: "79.99", CAD: "139.99", AUD: "159.99", INR: "6,999" },
      recommended: true,
      coverage: [
        "CONSERVATIVE – 216 prospects without buying intent, 152 with buying intent",
        "BALANCED – 104 prospects without buying intent, 86 with buying intent",
        "AGGRESSIVE – 104 prospects without buying intent, 86 with buying intent",
      ],
    },
    {
      id: 3,
      title: "Pro",
      credits: "2,000",
      prices: { USD: "199", GBP: "159.99", CAD: "279.99", AUD: "319.99", INR: "13,999" },
      recommended: false,
      coverage: [
        "CONSERVATIVE – 432 prospects without buying intent, 304 with buying intent",
        "BALANCED – 208 prospects without buying intent, 172 with buying intent",
        "AGGRESSIVE – 208 prospects without buying intent, 172 with buying intent",
      ],
    },
    {
      id: 4,
      title: "Enterprise",
      credits: "Custom",
      prices: { USD: "Custom", GBP: "Custom", CAD: "Custom", AUD: "Custom", INR: "Custom" },
      priceInfo: "Starts at $499+",
      features: [
        "Custom Credit Amount",
        "Dedicated Account Manager",
        "API Access & Support",
        "Custom Onboarding & Training",
      ],
      recommended: false,
    },
  ],
};

/* ---------- helpers ---------- */
const ICON_MAP = { Starter: Sparkles, Growth: Rocket, Pro: Zap, Enterprise: Building2 };
const ACCENT = {
  Starter: "from-violet-500/70 via-indigo-500/70 to-sky-500/70",
  Growth: "from-indigo-500/80 via-blue-500/80 to-sky-500/80",
  Pro: "from-sky-500/80 via-cyan-500/80 to-emerald-500/80",
  Enterprise: "from-slate-700/80 via-slate-800/80 to-slate-900/80",
};
const TAGLINE = {
  Starter: "Test autopilot on a focused batch of prospects.",
  Growth: "Scale your outbound with consistent weekly volume.",
  Pro: "Run multiple campaigns and channels at full throttle.",
  Enterprise: "For teams that need custom credits, SLAs and support.",
};
const chipColors = (label = "") => {
  const l = label.toLowerCase();
  if (l.startsWith("conservative"))
    return "bg-emerald-50 text-emerald-700 border-emerald-200";
  if (l.startsWith("balanced"))
    return "bg-indigo-50 text-indigo-700 border-indigo-200";
  if (l.startsWith("aggressive"))
    return "bg-rose-50 text-rose-700 border-rose-200";
  return "bg-slate-50 text-slate-700 border-slate-200";
};

function CurrencyPills({ selected, onSelect }) {
  const count = pricingData.currencies.length;
  const idx = Math.max(0, pricingData.currencies.findIndex((c) => c.id === selected));
  const widthCss = `calc((100% - 1rem) / ${count})`;
  const leftCss = `calc(${idx} * ((100% - 1rem) / ${count}) + 0.5rem)`;

  return (
    <div className="mt-8 flex justify-center">
      <div className="relative w-full max-w-3xl">
        <div className="relative rounded-full border border-slate-200 bg-white/80 shadow-[0_10px_40px_rgba(15,23,42,0.08)] backdrop-blur-md px-2 py-2">
          <div
            aria-hidden
            className="absolute top-2 bottom-2 rounded-full bg-slate-100 shadow-md ring-1 ring-slate-200 transition-all duration-300 ease-out"
            style={{ width: widthCss, left: leftCss }}
          />
          <div className="relative grid grid-cols-5">
            {pricingData.currencies.map((c) => {
              const isActive = selected === c.id;
              const country = c.name.split(" ")[0];
              const code = c.name.match(/\((.*?)\)/)?.[1] || c.id;
              return (
                <button
                  key={c.id}
                  onClick={() => onSelect(c.id)}
                  className="relative z-10 w-full flex items-center justify-center gap-3 rounded-full px-3 py-2.5 focus:outline-none focus-visible:ring-4 focus-visible:ring-indigo-300/50"
                  aria-pressed={isActive}
                  aria-label={`${country} (${code})`}
                >
                  <span
                    className={[
                      "flex h-9 w-9 items-center justify-center rounded-full border text-[11px] font-semibold",
                      isActive ? "bg-white border-slate-300" : "bg-slate-50 border-slate-200",
                    ].join(" ")}
                  >
                    {code}
                  </span>
                  <span className="text-left leading-tight">
                    <span
                      className={[
                        "block text-[13px] font-semibold tracking-wide",
                        isActive ? "text-slate-900" : "text-slate-700",
                      ].join(" ")}
                    >
                      {country}
                    </span>
                    <span
                      className={[
                        "block text-[11px] font-medium uppercase",
                        isActive ? "text-indigo-600" : "text-slate-400",
                      ].join(" ")}
                    >
                      {code}
                    </span>
                  </span>
                </button>
              );
            })}
          </div>
        </div>
        <div aria-hidden className="pointer-events-none absolute -top-3 left-10 right-10 h-6 rounded-full bg-indigo-100/70 blur-xl" />
      </div>
    </div>
  );
}

function PlanCard({ plan, selectedCurrency, onBuy }) {
  const symbol = pricingData.currencies.find((c) => c.id === selectedCurrency)?.symbol || "";
  const displayPrice = plan.prices?.[selectedCurrency] ?? "Custom";
  const isCustom = String(displayPrice).toLowerCase() === "custom";
  const Icon = ICON_MAP[plan.title] || Dot;

  return (
    <div
      className={`relative rounded-2xl p-6 border bg-white shadow-[0_18px_45px_rgba(15,23,42,0.08)] hover:-translate-y-1 transition-all duration-300 ring-1 ${
        plan.recommended
          ? "border-indigo-500 ring-indigo-200 hover:ring-indigo-300"
          : "border-slate-200 ring-slate-200/70 hover:border-slate-300"
      }`}
    >
      <div
        className={`pointer-events-none absolute -top-10 right-0 h-24 w-24 rounded-full blur-3xl opacity-40 bg-gradient-to-br ${ACCENT[plan.title]}`}
      />
      {plan.recommended && (
        <p className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gradient-to-r from-indigo-600 to-sky-500 text-white text-[11px] font-semibold px-3 py-1 rounded-full uppercase tracking-wider shadow-md">
          Most Popular
        </p>
      )}

      {/* header */}
      <div className="text-center relative z-10">
        <div className="mx-auto mb-4 inline-flex items-center justify-center rounded-2xl px-3 py-2 bg-slate-50 border border-slate-200 text-slate-700 shadow-sm">
          <Icon className="h-5 w-5 mr-1.5 text-indigo-500" />
          <span className="text-xs font-semibold uppercase tracking-wide text-slate-600">
            {plan.title === "Enterprise" ? "Custom for teams" : "Credits for autopilot"}
          </span>
        </div>
        <h3 className="text-[1.4rem] font-extrabold tracking-tight text-slate-900">
          {plan.title}
        </h3>
        <p className="mt-1 text-xs text-slate-500 max-w-xs mx-auto">{TAGLINE[plan.title]}</p>

        <p className="mt-4 text-4xl font-black tabular-nums text-slate-900">
          {!isCustom && symbol}
          {displayPrice}
          {!isCustom && selectedCurrency === "GBP" && (
            <span className="ml-1 align-super text-xs font-medium text-slate-400">+ VAT</span>
          )}
        </p>
        <p className="mt-1 text-sm font-semibold text-indigo-600">
          {plan.credits}{" "}
          <span className="text-slate-500 font-normal">
            credits •{" "}
            {plan.title === "Enterprise"
              ? "custom volume"
              : plan.title === "Starter"
              ? "ideal for early testing"
              : plan.title === "Growth"
              ? "for consistent monthly outreach"
              : "for heavy multi-campaign use"}
          </span>
        </p>
      </div>

      {/* coverage / features */}
      {plan.coverage && (
        <div className="mt-6 space-y-3 border-t border-slate-200 pt-5">
          <p className="font-semibold text-slate-800 text-xs flex items-center gap-2">
            <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-indigo-50 border border-indigo-100">
              <Rocket className="h-3 w-3 text-indigo-500" />
            </span>
            Autopilot coverage (approx prospects):
          </p>
          <div className="space-y-2">
            {plan.coverage.map((line, i) => {
              const [label, rest] = line.split("–");
              return (
                <div
                  key={i}
                  className="flex items-start gap-2 rounded-lg bg-slate-50 border border-slate-200 px-3 py-2"
                >
                  <span
                    className={
                      "inline-flex items-center px-2 py-0.5 rounded-full border text-[10px] font-semibold uppercase tracking-wide " +
                      chipColors(label)
                    }
                  >
                    {label.trim()}
                  </span>
                  <span className="text-[11px] text-slate-600 leading-5">– {rest?.trim()}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {plan.features && plan.title === "Enterprise" && (
        <div className="mt-6 space-y-2 border-t border-slate-200 pt-5">
          {plan.features.map((feat, i) => (
            <p key={i} className="flex items-start">
              <CheckCircle className="h-4 w-4 text-emerald-500 mr-2 mt-0.5 flex-shrink-0" />
              <span className="text-slate-700 text-[0.9rem] leading-6">{feat}</span>
            </p>
          ))}
          {plan.priceInfo && (
            <p className="text-left text-xs text-slate-500 mt-2">{plan.priceInfo}</p>
          )}
        </div>
      )}

      {/* CTA */}
      <div className="mt-6">
        {plan.title === "Enterprise" ? (
          <a
            href="mailto:sales@roboman.ai"
            className="w-full inline-flex items-center justify-center gap-2 py-3 px-6 rounded-lg font-semibold transition-all duration-300 bg-slate-900 text-white hover:bg-black hover:shadow-[0_16px_40px_rgba(15,23,42,0.35)]"
          >
            <Building2 className="h-4 w-4" />
            Contact Sales
          </a>
        ) : (
          <button
            onClick={onBuy}
            className="w-full inline-flex items-center justify-center gap-2 py-3 px-6 rounded-lg font-semibold bg-slate-100 text-slate-400 border border-slate-200"
          >
            <Zap className="h-4 w-4" />
            Buy Credits
          </button>
        )}
        {plan.title !== "Enterprise" && (
          <p className="mt-2 text-[11px] text-slate-500 text-center">
            One-time purchase • No subscription • Credits never auto-renew
          </p>
        )}
      </div>
    </div>
  );
}

/* ---------- Page ---------- */
export default function HomepagePricingPage() {
  const navigate = useNavigate();
  const [selectedCurrency, setSelectedCurrency] = useState("USD"); // screenshot shows USD active

  return (
    <section id="pricing" className="py-24 bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Hero / Pills */}
        <div className="text-center">
          <div className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-medium text-slate-700 shadow-sm">
            <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-indigo-50">
              <Sparkles className="h-3 w-3 text-indigo-500" />
            </span>
            <span>Credits-based Autopilot for B2B Outreach</span>
          </div>
          <h2 className="mt-4 text-3xl font-extrabold tracking-tight text-slate-900">
            Flexible Credit Packages
          </h2>
          <p className="mt-3 max-w-2xl mx-auto text-sm sm:text-base text-slate-500">
            Use credits across email, LinkedIn, proposals and voice notes. Turn on Conservative,
            Balanced or Aggressive autopilot — with or without buying intent scoring.
          </p>
          <CurrencyPills selected={selectedCurrency} onSelect={setSelectedCurrency} />
        </div>

        {/* Cards */}
        <div className="mt-12 grid lg:grid-cols-4 gap-7 items-stretch">
          {pricingData.plans.map((p) => (
            <PlanCard
              key={p.id}
              plan={p}
              selectedCurrency={selectedCurrency}
              onBuy={() => navigate("/signup")}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
